import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getLocale,
  getLocales,
  createLocale,
  updateLocale,
} from '../api/i18n.api';
import {
  ILocale,
  IGetLocale,
  IGetLocales,
  ILocaleReq,
} from '../models/i18n.model';

interface ILocaleState {
  locale?: ILocale;
  locales?: ILocale[];
  isLoading: boolean;
  error?: AxiosError | string;
}

const initialState: ILocaleState = {
  locale: undefined,
  locales: [],
  isLoading: false,
};

const localeSlicer = createSlice({
  name: 'locale',
  initialState,
  reducers: {
    getLocalesStart: (state: ILocaleState) => {
      state.locales = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getLocalesSuccess: (
      state: ILocaleState,
      action: PayloadAction<IGetLocales>,
    ) => {
      state.locales = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getLocalesFailure: (
      state: ILocaleState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.locales = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    getLocaleStart: (state: ILocaleState) => {
      state.locale = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getLocaleSuccess: (
      state: ILocaleState,
      action: PayloadAction<IGetLocale>,
    ) => {
      state.locale = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getLocaleFailure: (
      state: ILocaleState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.locale = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: ILocaleState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (
      state: ILocaleState,
      action: PayloadAction<AxiosError | string>,
    ) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: ILocaleState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    updateSuccess: (state: ILocaleState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  },
});

export const {
  getLocalesStart,
  getLocalesSuccess,
  getLocalesFailure,
  getLocaleStart,
  getLocaleSuccess,
  getLocaleFailure,
  requestStart,
  requestFailure,
  createSuccess,
  updateSuccess,
} = localeSlicer.actions;

export default localeSlicer.reducer;

export const doGetLocales = (): any => async (dispatch: any) => {
  try {
    dispatch(getLocalesStart());
    const response = await getLocales();
    dispatch(getLocalesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getLocalesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetLocale =
  (id: string): any =>
  async (dispatch: any) => {
    try {
      dispatch(getLocaleStart());
      const response = await getLocale(id);
      dispatch(getLocaleSuccess(response.data));
      return response;
    } catch (e: any) {
      dispatch(getLocaleFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doCreateLocale =
  (data: ILocaleReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await createLocale(data);
      dispatch(createSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };

export const doUpdateLocale =
  (id: string, data: ILocaleReq): any =>
  async (dispatch: any) => {
    try {
      dispatch(requestStart());
      const response = await updateLocale(id, data);
      dispatch(updateSuccess());
      return response;
    } catch (e: any) {
      dispatch(requestFailure(e.response?.data?.data || e.message));
      return e.response;
    }
  };
